// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xClassNames } from "@steiner/XDL/Utilities";
import { goBack } from "@steiner/XDL/Utilities/goBack";
import * as React from "react";
import { connect } from "react-redux";

// XNG-Changes: add fields for goBackLink + hidePageTitle
const enum fields {
    overrideTitle = "overrideTitle",
    showGoBack = "showGoBack",
    goBackTitle = "goBackTitle",
    goBackLink = "goBackLink",
    hidePageTitle = "hidePageTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.overrideTitle]: string;
        [fields.showGoBack]: boolean;
        [fields.goBackTitle]: string;
        [fields.goBackLink]: LinkFieldValue | string;
        [fields.hidePageTitle]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const currentPage = getCurrentPage(state);
    const isCheckoutPage = ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"].includes(currentPage.type);
    return {
        pageTitle: currentPage.fields.title,
        isCheckoutPage,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface PageTitleStyles {
    wrapper?: InjectableCss;
    title?: InjectableCss;
    goBackLink?: LinkPresentationProps;
}

export const pageTitleStyles: PageTitleStyles = {};

const styles = pageTitleStyles;
const PageTitle: React.FunctionComponent<Props> = ({ pageTitle, fields, isCheckoutPage }: Props) => {
    const { overrideTitle, showGoBack, goBackTitle, goBackLink, hidePageTitle } = fields;
    /**
     * We pass goBackLink as string when use createWidgetElement
     */
    const linkUrl = typeof goBackLink === "string" ? goBackLink : useGetLink(goBackLink).url;
    const history = useHistory();

    if (showGoBack) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Link
                    onClick={e => {
                        goBack(e, history, linkUrl);
                    }}
                    {...styles.goBackLink}
                    className={xClassNames.append(isCheckoutPage && "xSmall")}
                    aria-label={`${goBackTitle}`}
                    type="button"
                >
                    {goBackTitle}
                </Link>
                {!hidePageTitle && (
                    <Typography variant="h1" data-test-selector="PageTitle" {...styles.title}>
                        {overrideTitle || pageTitle}
                    </Typography>
                )}
            </StyledWrapper>
        );
    }

    return (
        <Typography variant="h1" data-test-selector="PageTitle" {...styles.title}>
            {overrideTitle || pageTitle}
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PageTitle),
    definition: {
        group: "Basic",
        icon: "heading",
        fieldDefinitions: [
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.showGoBack,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.goBackTitle,
                editorTemplate: "TextField",
                defaultValue: "Go Back",
                fieldType: "Translatable",
                isVisible: (item: HasFields) => item.fields.showGoBack,
            },
            {
                name: fields.goBackLink,
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields.showGoBack,
            },
            {
                name: fields.hidePageTitle,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
